import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { PersonBusinessInput, PersonBusinessResponse } from '@releve/shared';
import { supabase } from '@/integrations/supabase/client';
import { authedPost } from '@/lib/api';
import { useOrganization } from '@/contexts/OrganizationContext';
import { toast } from '@/hooks/useToast';
import i18n from '@/i18n';
import { personKeys } from '@/hooks/usePeople';

/**
 * The business identity a person edits on the "Business" tab when `is_business`
 * is checked. These map to the existing `business_vendor_identity` columns; they
 * are written to the person's own `businesses` row, never to `people` (SCRUM-161).
 */
export interface PersonBusinessIdentity {
  name: string;
  trading_name?: string | null;
  country_code?: string | null;
  legal_form_id?: string | null;
  registration_number?: string | null;
  vat_number?: string | null;
  iban?: string | null;
  bic?: string | null;
}

export interface LinkedBusinessIdentity {
  trading_name: string | null;
  country_code: string | null;
  legal_form_id: string | null;
  registration_number: string | null;
  vat_number: string | null;
  iban: string | null;
  bic: string | null;
}

export interface LinkedPersonBusiness {
  id: string;
  name: string;
  identity: LinkedBusinessIdentity | null;
}

/**
 * Fetch the `businesses` row (name + its one-to-one vendor identity) linked to a
 * person, to pre-fill the Business tab when editing an existing "is business"
 * person. Returns a normalized { id, name, identity } shape.
 */
export function useLinkedPersonBusiness(businessId?: string | null) {
  return useQuery<LinkedPersonBusiness | null>({
    queryKey: ['linked-person-business', businessId],
    queryFn: async () => {
      if (!businessId) return null;
      const { data, error } = await supabase
        .from('businesses')
        .select('id, name, business_vendor_identity(*)')
        .eq('id', businessId)
        .maybeSingle();
      if (error) throw error;
      if (!data) return null;
      // `business_vendor_identity.business_id` is UNIQUE, so PostgREST returns the
      // embed as a single object (to-one), not an array. Normalize defensively so
      // callers never have to guess the shape (reading it as `[0]` silently lost
      // the identity and could null it out on save).
      const embed = (data as { business_vendor_identity?: unknown }).business_vendor_identity;
      const identity = (Array.isArray(embed) ? embed[0] : embed) as
        | LinkedBusinessIdentity
        | null
        | undefined;
      return { id: data.id, name: data.name, identity: identity ?? null };
    },
    enabled: !!businessId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Create (or, for an already-linked person, update) the `businesses` +
 * `business_vendor_identity` + `business_contacts` rows for a person who is also
 * a business, atomically, via `POST /api/v1/people/orgs/:orgId/people/:personId/business`
 * (the `upsert_person_business` SECURITY INVOKER RPC behind it). Sets
 * `people.is_business` + `linked_business_id`. Returns the business id. The
 * org is the selected organization; a person outside it is a 404.
 */
export function useUpsertPersonBusiness() {
  const queryClient = useQueryClient();
  const { selectedOrganization } = useOrganization();
  const organizationId = selectedOrganization?.id;

  return useMutation({
    mutationFn: async ({
      personId,
      identity,
    }: {
      personId: string;
      identity: PersonBusinessIdentity;
    }): Promise<string> => {
      if (!organizationId) throw new Error('No organization selected');
      const body: PersonBusinessInput = {
        name: identity.name,
        trading_name: identity.trading_name ?? null,
        country_code: identity.country_code ?? null,
        legal_form_id: identity.legal_form_id ?? null,
        registration_number: identity.registration_number ?? null,
        vat_number: identity.vat_number ?? null,
        iban: identity.iban ?? null,
        bic: identity.bic ?? null,
      };
      const result = await authedPost<PersonBusinessResponse>(
        `/api/v1/people/orgs/${organizationId}/people/${personId}/business`,
        body,
      );
      return result.business_id;
    },
    onSuccess: (businessId) => {
      // The RPC creates a business + a business_contacts row → person_index
      // business_ids / organization_names, and the business index.
      void queryClient.invalidateQueries({ queryKey: personKeys.org(organizationId) });
      void queryClient.invalidateQueries({ queryKey: ['businesses'] });
      void queryClient.invalidateQueries({ queryKey: ['linked-person-business', businessId] });
      void queryClient.invalidateQueries({ queryKey: ['business-vendor-identity', businessId] });
    },
    onError: (error: Error) => {
      toast.error(
        i18n.t('common:people.toast.businessSaveFailed'),
        error.message ? { description: error.message } : undefined,
      );
    },
  });
}
